import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, useCallback, createContext, useContext } from "react";
import LandingPage from "@/pages/LandingPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminLogin from "@/pages/AdminLogin";
import ClientAuth from "@/pages/ClientAuth";
import ClientPortal from "@/pages/ClientPortal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Shared fetch helper — uses Authorization header, no credentials:'include'
export function authFetch(url, options = {}) {
  const token = sessionStorage.getItem('access_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
}

// Auth Context
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }

      const response = await authFetch(`${API}/auth/me`);
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(false);
        sessionStorage.removeItem('access_token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(false);
      sessionStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }
    
    const userData = await response.json();
    if (userData.access_token) {
      sessionStorage.setItem('access_token', userData.access_token);
    }
    setUser(userData);
    return userData;
  };

  const clientLogin = async (email, password) => {
    const response = await fetch(`${API}/client/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }
    
    const userData = await response.json();
    if (userData.access_token) {
      sessionStorage.setItem('access_token', userData.access_token);
    }
    setUser(userData);
    return userData;
  };

  const clientRegister = async (name, email, phone, password) => {
    const response = await fetch(`${API}/client/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, phone, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }
    
    const userData = await response.json();
    if (userData.access_token) {
      sessionStorage.setItem('access_token', userData.access_token);
    }
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    }
    sessionStorage.removeItem('access_token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, clientLogin, clientRegister, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-healing-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-healing-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-healing-burgundy-light font-body">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user || user.role !== 'admin') {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
}

function ClientProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-healing-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-healing-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-healing-burgundy-light font-body">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user || user.role !== 'client') {
    return <Navigate to="/account" replace />;
  }
  
  return children;
}

function App() {
  return (
    <div className="App min-h-screen bg-healing-cream">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="/account" element={<ClientAuth />} />
            <Route 
              path="/my-bookings" 
              element={
                <ClientProtectedRoute>
                  <ClientPortal />
                </ClientProtectedRoute>
              } 
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
